import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  BarChart2,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  Users,
  MapPin,
  Car,
  Wifi,
  DollarSign,
  CreditCard,
  Clock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileSpreadsheet,
  ChevronRight,
  Loader2,
  Truck,
  Building,
} from "lucide-react";
import * as XLSX from "xlsx";
import { FoodDeliveryOrdersReport } from "./FoodDeliveryOrdersReport";
import { SubscriptionPaymentsReport } from "./SubscriptionPaymentsReport";
import { DriverBankDetailsReport } from "./DriverBankDetailsReport";
import { RazorpayPaymentsReport } from "./RazorpayPaymentsReport";

// Period shortcuts for quick date selection
const PERIOD_SHORTCUTS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
];

export const ReportsManagement = () => {
  const { user } = useAuth();
  const [activeReport, setActiveReport] = useState(null);
  
  const reports = [
    {
      id: "razorpay-payments",
      name: "All Rider Razorpay Payments",
      description: "View all Razorpay payments made by riders - Commission, Subscription, and Food Delivery",
      icon: CreditCard,
    },
    {
      id: "subscription-payments",
      name: "Subscription Payments Report",
      description: "View all subscription payments received from riders with filters and pagination",
      icon: CreditCard,
    },
    {
      id: "driver-bank-details",
      name: "Driver Bank Details Report",
      description: "View all drivers and their bank details for earnings payments",
      icon: Building,
    },
    {
      id: "food-delivery-orders",
      name: "Food Delivery Orders Report",
      description: "View all food delivery orders with details, filters, and pagination",
      icon: Truck,
    },
    {
      id: "manager-performance",
      name: "Manager Performance Report",
      description: "Track manager performance across cities with commission and driver metrics",
      icon: Users,
    },
    {
      id: "city-performance",
      name: "City Performance Report",
      description: "Daily rides and riders count by city for the selected month (pivot table)",
      icon: MapPin,
    },
  ];
  
  return (
    <div className="space-y-6" data-testid="reports-management">
      {!activeReport ? (
        <>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <BarChart2 className="w-7 h-7 text-primary" />
                Reports
              </h2>
              <p className="text-muted-foreground mt-1">
                View and download detailed reports for your business
              </p>
            </div>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {reports.map((report) => (
              <Card 
                key={report.id}
                className="cursor-pointer hover:border-primary transition-colors"
                onClick={() => setActiveReport(report.id)}
                data-testid={`report-card-${report.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <report.icon className="w-5 h-5 text-primary" />
                    {report.name}
                  </CardTitle>
                  <CardDescription>{report.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="outline" className="w-full">
                    View Report
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : activeReport === "razorpay-payments" ? (
        <RazorpayPaymentsReport onBack={() => setActiveReport(null)} />
      ) : activeReport === "subscription-payments" ? (
        <SubscriptionPaymentsReport onBack={() => setActiveReport(null)} />
      ) : activeReport === "driver-bank-details" ? (
        <DriverBankDetailsReport onBack={() => setActiveReport(null)} />
      ) : activeReport === "food-delivery-orders" ? (
        <FoodDeliveryOrdersReport onBack={() => setActiveReport(null)} />
      ) : activeReport === "manager-performance" ? (
        <ManagerPerformanceReport onBack={() => setActiveReport(null)} />
      ) : activeReport === "city-performance" ? (
        <CityPerformanceReport onBack={() => setActiveReport(null)} />
      ) : null}
    </div>
  );
};

// Manager Performance Report Component
const ManagerPerformanceReport = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  
  // Filters
  const [manager, setManager] = useState("");
  const [cityId, setCityId] = useState("");
  const [period, setPeriod] = useState("this_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Filter options
  const [managers, setManagers] = useState([]);
  const [cities, setCities] = useState([]);
  
  // Sorting
  const [sortBy, setSortBy] = useState("manager");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Fetch filter options
  useEffect(() => {
    fetchFilterOptions();
  }, [user?.id]);
  
  const fetchFilterOptions = async () => {
    if (!user?.id) return;
    
    try {
      // Fetch managers
      const managersRes = await axios.get(`${API}/reports/managers?admin_id=${user.id}`);
      setManagers(managersRes.data.managers || []);
      
      // Fetch cities and sort alphabetically
      const citiesRes = await axios.get(`${API}/cities?admin_id=${user.id}`);
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to fetch filter options:", err);
    }
  };
  
  const fetchReport = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ admin_id: user.id });
      
      if (manager) params.append("manager", manager);
      if (cityId) params.append("city_id", cityId);
      if (period) params.append("period", period);
      if (startDate && !period) params.append("start_date", startDate);
      if (endDate && !period) params.append("end_date", endDate);
      
      const res = await axios.get(`${API}/reports/manager-performance?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  }, [user?.id, manager, cityId, period, startDate, endDate]);
  
  // Fetch on mount and filter change
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);
  
  // Handle period change
  const handlePeriodChange = (value) => {
    setPeriod(value);
    if (value) {
      setStartDate("");
      setEndDate("");
    }
  };
  
  // Handle custom date change
  const handleCustomDateChange = (type, value) => {
    if (type === "start") setStartDate(value);
    else setEndDate(value);
    setPeriod(""); // Clear period when using custom dates
  };
  
  // Sort data
  const sortedRows = data?.rows ? [...data.rows].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    
    // Handle string comparison
    if (typeof aVal === "string") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortOrder === "asc") {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  }) : [];
  
  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };
  
  // Export to Excel
  const exportToExcel = () => {
    if (!data?.rows?.length) {
      toast.error("No data to export");
      return;
    }
    
    const exportData = sortedRows.map(row => ({
      "Manager": row.manager,
      "City": row.city,
      "Drivers": row.drivers_count,
      "Online Now": row.drivers_online,
      "Rides": row.rides_count,
      "Commission Earned (₹)": row.commission_earned,
      "Commission Paid (₹)": row.commission_paid,
      "Commission Due (₹)": row.commission_due,
    }));
    
    // Add totals row
    exportData.push({
      "Manager": "TOTAL",
      "City": "",
      "Drivers": data.totals.drivers_count,
      "Online Now": data.totals.drivers_online,
      "Rides": data.totals.rides_count,
      "Commission Earned (₹)": data.totals.commission_earned,
      "Commission Paid (₹)": data.totals.commission_paid,
      "Commission Due (₹)": data.totals.commission_due,
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Manager Performance");
    
    // Generate filename with date
    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(wb, `Manager_Performance_Report_${dateStr}.xlsx`);
    
    toast.success("Report exported successfully");
  };
  
  // Render sort icon
  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown className="w-4 h-4 text-muted-foreground" />;
    return sortOrder === "asc" 
      ? <ArrowUp className="w-4 h-4 text-primary" />
      : <ArrowDown className="w-4 h-4 text-primary" />;
  };
  
  return (
    <div className="space-y-6" data-testid="manager-performance-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-2">
            ← Back to Reports
          </Button>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Users className="w-7 h-7 text-primary" />
            Manager Performance Report
          </h2>
          <p className="text-muted-foreground mt-1">
            Track manager performance across cities
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={exportToExcel} disabled={!data?.rows?.length}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* Manager Filter */}
            <div className="space-y-2">
              <Label>Manager</Label>
              <Select value={manager || "all"} onValueChange={(v) => setManager(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-manager">
                  <SelectValue placeholder="All Managers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* City Filter */}
            <div className="space-y-2">
              <Label>City</Label>
              <Select value={cityId || "all"} onValueChange={(v) => setCityId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Period Shortcuts */}
            <div className="space-y-2">
              <Label>Period</Label>
              <Select value={period} onValueChange={handlePeriodChange}>
                <SelectTrigger data-testid="filter-period">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_SHORTCUTS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Custom Date Range */}
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => handleCustomDateChange("start", e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => handleCustomDateChange("end", e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Report Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Report Data
            </span>
            {data && (
              <span className="text-sm font-normal text-muted-foreground">
                Generated: {new Date(data.generated_at).toLocaleString()}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !data?.rows?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              No data found for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("manager")}
                    >
                      <div className="flex items-center gap-2">
                        Manager
                        <SortIcon column="manager" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleSort("city")}
                    >
                      <div className="flex items-center gap-2">
                        City
                        <SortIcon column="city" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("drivers_count")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Drivers
                        <SortIcon column="drivers_count" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("drivers_online")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Online
                        <SortIcon column="drivers_online" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("rides_count")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Rides
                        <SortIcon column="rides_count" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("commission_earned")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Earned (₹)
                        <SortIcon column="commission_earned" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("commission_paid")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Paid (₹)
                        <SortIcon column="commission_paid" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50 text-right"
                      onClick={() => handleSort("commission_due")}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Due (₹)
                        <SortIcon column="commission_due" />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((row, index) => (
                    <TableRow key={`${row.city_id}-${index}`}>
                      <TableCell className="font-medium">{row.manager}</TableCell>
                      <TableCell>{row.city}</TableCell>
                      <TableCell className="text-right">{row.drivers_count}</TableCell>
                      <TableCell className="text-right">
                        <span className={row.drivers_online > 0 ? "text-green-600 font-medium" : ""}>
                          {row.drivers_online}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{row.rides_count}</TableCell>
                      <TableCell className="text-right font-mono">
                        ₹{row.commission_earned.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        ₹{row.commission_paid.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        <span className={row.commission_due > 0 ? "text-amber-600 font-semibold" : ""}>
                          ₹{row.commission_due.toLocaleString()}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell colSpan={2}>TOTAL</TableCell>
                    <TableCell className="text-right">{data.totals.drivers_count}</TableCell>
                    <TableCell className="text-right text-green-600">{data.totals.drivers_online}</TableCell>
                    <TableCell className="text-right">{data.totals.rides_count}</TableCell>
                    <TableCell className="text-right font-mono">
                      ₹{data.totals.commission_earned.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      ₹{data.totals.commission_paid.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-amber-600">
                      ₹{data.totals.commission_due.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// City Performance Report Component (Pivot Table Style)
const CityPerformanceReport = ({ onBack }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  
  // Filters
  const [cityId, setCityId] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  
  // Filter options
  const [cities, setCities] = useState([]);
  
  // Generate year options (last 3 years + current)
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];
  
  // Month options
  const monthOptions = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];
  
  // Fetch filter options
  useEffect(() => {
    fetchFilterOptions();
  }, [user?.id]);
  
  const fetchFilterOptions = async () => {
    if (!user?.id) return;
    try {
      const citiesRes = await axios.get(`${API}/cities?admin_id=${user.id}`);
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to fetch cities:", err);
    }
  };
  
  const fetchReport = useCallback(async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        admin_id: user.id,
        year: year.toString(),
        month: month.toString()
      });
      
      if (cityId) params.append("city_id", cityId);
      
      const res = await axios.get(`${API}/reports/city-performance?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  }, [user?.id, year, month, cityId]);
  
  // Fetch on mount and filter change
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);
  
  // Export to Excel
  const exportToExcel = () => {
    if (!data?.rows?.length) {
      toast.error("No data to export");
      return;
    }
    
    const daysInMonth = data.days_in_month || 31;
    
    // Build export data
    const exportData = data.rows.map(row => {
      const rowData = {
        "City": row.city,
        "Measure": row.measure,
      };
      for (let d = 1; d <= daysInMonth; d++) {
        rowData[`Day ${d}`] = row[`day${d}`] || 0;
      }
      rowData["Total"] = row.total || 0;
      return rowData;
    });
    
    // Add totals rows
    if (data.totals) {
      const ridesTotal = { "City": "TOTAL", "Measure": "Rides" };
      const ridersTotal = { "City": "TOTAL", "Measure": "Riders" };
      for (let d = 1; d <= daysInMonth; d++) {
        ridesTotal[`Day ${d}`] = data.totals.rides[`day${d}`] || 0;
        ridersTotal[`Day ${d}`] = data.totals.riders[`day${d}`] || 0;
      }
      ridesTotal["Total"] = data.totals.rides.total || 0;
      ridersTotal["Total"] = data.totals.riders.total || 0;
      exportData.push(ridesTotal);
      exportData.push(ridersTotal);
    }
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "City Performance");
    
    const monthName = monthOptions.find(m => m.value === month)?.label || month;
    XLSX.writeFile(wb, `City_Performance_${monthName}_${year}.xlsx`);
    
    toast.success("Report exported successfully");
  };
  
  const daysInMonth = data?.days_in_month || 31;
  
  // Group rows by city for better display
  const groupedRows = [];
  if (data?.rows) {
    for (let i = 0; i < data.rows.length; i += 2) {
      const ridesRow = data.rows[i];
      const ridersRow = data.rows[i + 1];
      if (ridesRow && ridersRow) {
        groupedRows.push({ city: ridesRow.city, city_id: ridesRow.city_id, rides: ridesRow, riders: ridersRow });
      }
    }
  }
  
  return (
    <div className="space-y-6" data-testid="city-performance-report">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-2">
            ← Back to Reports
          </Button>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <MapPin className="w-7 h-7 text-primary" />
            City Performance Report
          </h2>
          <p className="text-muted-foreground mt-1">
            Daily rides and riders count by city
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={exportToExcel} disabled={!data?.rows?.length}>
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>
      
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {/* Year */}
            <div className="space-y-2">
              <Label>Year</Label>
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger data-testid="filter-year">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Month */}
            <div className="space-y-2">
              <Label>Month</Label>
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger data-testid="filter-month">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* City Filter */}
            <div className="space-y-2">
              <Label>City</Label>
              <Select value={cityId || "all"} onValueChange={(v) => setCityId(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filter-city">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Report Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              {data?.filters_applied?.month_name} {year}
            </span>
            {data && (
              <span className="text-sm font-normal text-muted-foreground">
                Generated: {new Date(data.generated_at).toLocaleString()}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : !data?.rows?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              No data found for the selected filters
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background z-10 min-w-[120px]">City</TableHead>
                    <TableHead className="sticky left-[120px] bg-background z-10 min-w-[80px]">Measure</TableHead>
                    {Array.from({ length: daysInMonth }, (_, i) => (
                      <TableHead key={i + 1} className="text-center min-w-[50px]">
                        {i + 1}
                      </TableHead>
                    ))}
                    <TableHead className="text-right font-bold min-w-[70px]">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedRows.map((group, idx) => (
                    <>
                      {/* Rides Row */}
                      <TableRow key={`${group.city_id}-rides`} className="border-t-2 border-muted">
                        <TableCell rowSpan={2} className="sticky left-0 bg-background z-10 font-medium align-middle">
                          {group.city}
                        </TableCell>
                        <TableCell className="sticky left-[120px] bg-background z-10 text-xs text-blue-600 font-medium">
                          Rides
                        </TableCell>
                        {Array.from({ length: daysInMonth }, (_, i) => (
                          <TableCell key={i + 1} className="text-center text-sm">
                            {group.rides[`day${i + 1}`] || 0}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-bold text-blue-600">
                          {group.rides.total || 0}
                        </TableCell>
                      </TableRow>
                      {/* Riders Row */}
                      <TableRow key={`${group.city_id}-riders`}>
                        <TableCell className="sticky left-[120px] bg-background z-10 text-xs text-green-600 font-medium">
                          Riders
                        </TableCell>
                        {Array.from({ length: daysInMonth }, (_, i) => (
                          <TableCell key={i + 1} className="text-center text-sm">
                            {group.riders[`day${i + 1}`] || 0}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-bold text-green-600">
                          {group.riders.total || 0}
                        </TableCell>
                      </TableRow>
                    </>
                  ))}
                </TableBody>
                {data.totals && (
                  <TableFooter>
                    {/* Totals - Rides */}
                    <TableRow className="bg-muted/50 font-bold border-t-4 border-primary">
                      <TableCell rowSpan={2} className="sticky left-0 bg-muted/50 z-10 font-bold align-middle">
                        TOTAL
                      </TableCell>
                      <TableCell className="sticky left-[120px] bg-muted/50 z-10 text-xs text-blue-600 font-bold">
                        Rides
                      </TableCell>
                      {Array.from({ length: daysInMonth }, (_, i) => (
                        <TableCell key={i + 1} className="text-center text-sm font-bold">
                          {data.totals.rides[`day${i + 1}`] || 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold text-blue-600">
                        {data.totals.rides.total || 0}
                      </TableCell>
                    </TableRow>
                    {/* Totals - Riders */}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell className="sticky left-[120px] bg-muted/50 z-10 text-xs text-green-600 font-bold">
                        Riders
                      </TableCell>
                      {Array.from({ length: daysInMonth }, (_, i) => (
                        <TableCell key={i + 1} className="text-center text-sm font-bold">
                          {data.totals.riders[`day${i + 1}`] || 0}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold text-green-600">
                        {data.totals.riders.total || 0}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportsManagement;
